/**
 * ZAXIA Arrival Experience (Flow 0)
 * Phase D5.1: Premium welcome experience for arriving guests
 *
 * FLOW:
 * 0. Tap to Begin - Required for browser audio autoplay policy
 * 1. Boot Screen (3.5s) - ZAXIA logo animation on black
 * 2. Welcome Screen - Personalized greeting spoken via Azure TTS
 * 3. Transition - "How can I help you today?" → Main app
 *
 * DESIGN NOTES:
 * - Tap to begin required for audio (browser autoplay policy)
 * - Guest name from backend (single source of truth)
 * - Must replay on demo reset, NOT on in-app navigation
 * - Uses Azure TTS (Thomas voice) for speech
 */

import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { zaxiaVoice } from '@/services/ZaxiaVoiceService';
import { useIsTV } from '@/hooks';

// =============================================================================
// TYPES
// =============================================================================

interface ArrivalExperienceProps {
  /** Guest's first name from backend */
  guestFirstName: string;
  /** Hotel name for context */
  hotelName: string;
  /** Callback when arrival flow is complete */
  onComplete: () => void;
}

type ArrivalPhase = 'tap-to-begin' | 'boot' | 'welcome' | 'transition';

// =============================================================================
// TIMING CONSTANTS
// =============================================================================

const BOOT_DURATION_MS = 5500; // 5.5 seconds for logo animation (premium feel)
const WELCOME_TEXT_DELAY_MS = 800; // Delay before showing welcome text
const WELCOME_SPEECH_DELAY_MS = 1500; // Longer delay before speaking to let visuals settle
const POST_WELCOME_PAUSE_MS = 800; // Pause after welcome speech before transition
const TRANSITION_TEXT_DELAY_MS = 300; // Delay before showing transition text
const TRANSITION_SPEECH_DELAY_MS = 500; // Delay before transition speech
const POST_TRANSITION_PAUSE_MS = 1000; // 1 second pause after transition before main app

// =============================================================================
// COMPONENT
// =============================================================================

export function ArrivalExperience({
  guestFirstName,
  hotelName,
  onComplete,
}: ArrivalExperienceProps) {
  const [phase, setPhase] = useState<ArrivalPhase>('tap-to-begin');
  const [, setLogoOpacity] = useState(0);
  const [welcomeTextVisible, setWelcomeTextVisible] = useState(false);
  const [transitionTextVisible, setTransitionTextVisible] = useState(false);
  const hasSpokenWelcomeRef = useRef(false);
  const hasSpokenTransitionRef = useRef(false);

  // P2.8-3b: TV mode detection for typography + safe-area treatment
  const isTV = useIsTV();

  // Reset refs when component mounts (for demo reset replay)
  useEffect(() => {
    hasSpokenWelcomeRef.current = false;
    hasSpokenTransitionRef.current = false;
    console.log('[ZAXIA Arrival] Component mounted, refs reset');
  }, []);

  // ==========================================================================
  // Tap to Begin Handler - Required for browser audio autoplay policy
  // ==========================================================================
  const handleTapToBegin = () => {
    console.log('[ZAXIA Arrival] User tapped to begin - audio unlocked');
    setPhase('boot');
  };

  // ==========================================================================
  // Boot Phase - Logo animation
  // ==========================================================================
  useEffect(() => {
    if (phase !== 'boot') return;

    console.log('[ZAXIA Arrival] Phase: boot');

    // Fade in logo
    const fadeInTimer = setTimeout(() => {
      setLogoOpacity(1);
    }, 100);

    // Start fade out 1 second before video ends
    const fadeOutTimer = setTimeout(() => {
      setLogoOpacity(0);
    }, BOOT_DURATION_MS - 1000);

    // After boot duration, move to welcome phase
    const bootTimer = setTimeout(() => {
      setPhase('welcome');
    }, BOOT_DURATION_MS);

    return () => {
      clearTimeout(fadeInTimer);
      clearTimeout(fadeOutTimer);
      clearTimeout(bootTimer);
    };
  }, [phase]);

  // ==========================================================================
  // Welcome Phase - Personalized greeting with TTS
  // ==========================================================================
  useEffect(() => {
    if (phase !== 'welcome') return;
    if (hasSpokenWelcomeRef.current) return; // Prevent double-speak on re-render
    hasSpokenWelcomeRef.current = true;

    console.log('[ZAXIA Arrival] Phase: welcome');

    // Show welcome text with delay for visual impact
    const showTextTimer = setTimeout(() => {
      setWelcomeTextVisible(true);
    }, WELCOME_TEXT_DELAY_MS);

    // Speak welcome message after text is visible
    const speakTimer = setTimeout(() => {
      const welcomeMessage = `Welcome to ${hotelName} ${guestFirstName}. I'm ZAXIA, your personal concierge.`;
      console.log('[ZAXIA Arrival] Speaking welcome:', welcomeMessage);

      zaxiaVoice.speak(welcomeMessage, {
        onEnd: () => {
          console.log('[ZAXIA Arrival] Welcome speech complete, pausing before transition');
          // Give time for the message to sink in
          setTimeout(() => {
            setPhase('transition');
          }, POST_WELCOME_PAUSE_MS);
        },
        onError: (error) => {
          console.error('[ZAXIA Arrival] Speech error:', error);
          // Move to transition even if speech fails, with longer delay
          setTimeout(() => setPhase('transition'), 3000);
        },
      });
    }, WELCOME_SPEECH_DELAY_MS);

    return () => {
      clearTimeout(showTextTimer);
      clearTimeout(speakTimer);
    };
  }, [phase, guestFirstName, hotelName]);

  // ==========================================================================
  // Transition Phase - "How can I help you today?"
  // ==========================================================================
  useEffect(() => {
    if (phase !== 'transition') return;
    if (hasSpokenTransitionRef.current) return; // Prevent double-speak
    hasSpokenTransitionRef.current = true;

    console.log('[ZAXIA Arrival] Phase: transition');

    // Show transition text with delay
    const showTextTimer = setTimeout(() => {
      setTransitionTextVisible(true);
    }, TRANSITION_TEXT_DELAY_MS);

    // Speak transition message after text appears
    const speakTimer = setTimeout(() => {
      const transitionMessage = "I'm here to help you discover everything around you and make your stay perfect.";
      console.log('[ZAXIA Arrival] Speaking transition:', transitionMessage);

      zaxiaVoice.speak(transitionMessage, {
        onEnd: () => {
          console.log('[ZAXIA Arrival] Transition speech complete, completing flow');
          // Longer pause to let the question hang before showing main app
          setTimeout(() => {
            onComplete();
          }, POST_TRANSITION_PAUSE_MS);
        },
        onError: () => {
          // Complete even if speech fails, with longer delay
          setTimeout(() => {
            onComplete();
          }, 3000);
        },
      });
    }, TRANSITION_SPEECH_DELAY_MS);

    return () => {
      clearTimeout(showTextTimer);
      clearTimeout(speakTimer);
    };
  }, [phase, onComplete]);

  // ==========================================================================
  // Render
  // ==========================================================================
  return (
    <div
      className="fixed inset-0 z-50 bg-black flex items-center justify-center overflow-hidden"
      data-tv-mode={isTV ? 'true' : undefined}
      style={isTV ? { padding: 'clamp(2rem, 8vh, 6rem) clamp(2rem, 8vw, 8rem)' } : undefined}
    >
      {/* Tap to Begin Phase - Required for audio autoplay policy */}
      {phase === 'tap-to-begin' && (
        <button
          onClick={handleTapToBegin}
          className="flex flex-col items-center justify-center cursor-pointer focus:outline-none group"
        >
          {/* ZAXIA Logo */}
          <div className="relative">
            {/* Glow effect */}
            <div className="absolute inset-0 blur-3xl opacity-20 group-hover:opacity-40 bg-gradient-to-r from-cyan-400 to-purple-500 rounded-full scale-150 transition-opacity duration-500" />

            {/* Logo text */}
            <h1 className="relative text-7xl font-bold tracking-widest text-white">
              <span className="bg-gradient-to-r from-cyan-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
                ZAXIA
              </span>
            </h1>
          </div>

          {/* Subtitle */}
          <p className="mt-4 text-white/40 text-sm tracking-[0.3em] uppercase">
            Personal Concierge
          </p>

          {/* Tap to begin prompt */}
          <div className="mt-12 flex flex-col items-center">
            <div className="w-16 h-16 rounded-full border-2 border-cyan-400/50 flex items-center justify-center group-hover:border-cyan-400 group-hover:bg-cyan-400/10 transition-all duration-300">
              <svg
                className="w-8 h-8 text-cyan-400/70 group-hover:text-cyan-400 transition-colors"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <p className="mt-4 text-white/50 text-sm tracking-wide animate-pulse">
              Tap to begin
            </p>
          </div>
        </button>
      )}

      {/* Boot Phase - Logo */}
      {phase === 'boot' && (
        <div className="flex flex-col items-center justify-center">
          {/* ZAXIA Logo */}
          <div className="relative">
            {/* Animated Glow effect - pulsing and expanding */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{
                opacity: [0, 0.3, 0.4, 0.3],
                scale: [0.8, 1.2, 1.5, 1.2]
              }}
              transition={{
                duration: 2,
                times: [0, 0.3, 0.6, 1],
                repeat: Infinity,
                repeatType: "reverse",
                ease: "easeInOut"
              }}
              className="absolute inset-0 blur-3xl bg-gradient-to-r from-cyan-400 to-purple-500 rounded-full"
            />

            {/* Logo text with staggered letter animation */}
            <h1 className="relative text-7xl font-bold tracking-widest text-white flex">
              {['Z', 'A', 'X', 'I', 'A'].map((letter, index) => (
                <motion.span
                  key={letter + index}
                  initial={{
                    opacity: 0,
                    y: -50,
                    rotateX: -90,
                    scale: 0.5
                  }}
                  animate={{
                    opacity: 1,
                    y: 0,
                    rotateX: 0,
                    scale: 1
                  }}
                  transition={{
                    duration: 1.2,
                    delay: index * 0.3,
                    ease: [0.34, 1.56, 0.64, 1], // Bouncy easing
                  }}
                  className="bg-gradient-to-b from-cyan-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent"
                  style={{
                    display: 'inline-block',
                    filter: 'drop-shadow(0 0 20px rgba(103, 232, 249, 0.5))',
                  }}
                >
                  {letter}
                </motion.span>
              ))}
            </h1>

            {/* Orbiting particles */}
            <motion.div
              animate={{ rotate: 360 }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "linear"
              }}
              className="absolute inset-0 w-full h-full"
            >
              <div className="absolute top-0 left-1/2 w-2 h-2 rounded-full bg-cyan-400 shadow-lg shadow-cyan-400/50" />
              <div className="absolute bottom-0 left-1/2 w-2 h-2 rounded-full bg-purple-400 shadow-lg shadow-purple-400/50" />
            </motion.div>
          </div>

          {/* Subtitle with fade-in */}
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 0.4, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-4 text-white text-sm tracking-[0.3em] uppercase"
          >
            Personal Concierge
          </motion.p>

          {/* Loading dots animation with wave effect */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 1.5 }}
            className="mt-8 flex space-x-2"
          >
            {[0, 150, 300].map((delay, index) => (
              <motion.div
                key={index}
                animate={{
                  scale: [1, 1.3, 1],
                  opacity: [0.4, 1, 0.4]
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  delay: delay / 1000,
                  ease: "easeInOut"
                }}
                className={`w-2 h-2 rounded-full ${
                  index === 1 ? 'bg-purple-400' : 'bg-cyan-400'
                }`}
              />
            ))}
          </motion.div>
        </div>
      )}

      {/* Welcome Phase - Personalized Greeting */}
      {phase === 'welcome' && (
        <div className="flex flex-col items-center justify-center px-8 text-center">
          {/* Avatar or icon */}
          <div
            className={`w-24 h-24 rounded-full bg-gradient-to-br from-cyan-500 to-purple-600 flex items-center justify-center mb-8 transition-all duration-700 ${
              welcomeTextVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
            }`}
          >
            <svg
              className="w-12 h-12 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
              />
            </svg>
          </div>

          {/* Welcome text */}
          <h1
            className={`text-4xl md:text-5xl font-light text-white mb-4 transition-all duration-700 ${
              welcomeTextVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            Welcome, <span className="font-semibold">{guestFirstName}</span>
          </h1>

          <p
            className={`text-xl text-white/60 transition-all duration-700 delay-200 ${
              welcomeTextVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            to {hotelName}
          </p>

          {/* Speaking indicator */}
          <div
            className={`mt-8 flex items-center space-x-3 text-cyan-400 transition-all duration-500 delay-500 ${
              welcomeTextVisible ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <div className="flex space-x-1">
              <div className="w-1 h-6 bg-cyan-400 rounded-full animate-pulse" style={{ animationDuration: '0.8s' }} />
              <div className="w-1 h-8 bg-cyan-400 rounded-full animate-pulse" style={{ animationDuration: '0.6s', animationDelay: '0.1s' }} />
              <div className="w-1 h-5 bg-cyan-400 rounded-full animate-pulse" style={{ animationDuration: '0.7s', animationDelay: '0.2s' }} />
              <div className="w-1 h-7 bg-cyan-400 rounded-full animate-pulse" style={{ animationDuration: '0.5s', animationDelay: '0.15s' }} />
              <div className="w-1 h-4 bg-cyan-400 rounded-full animate-pulse" style={{ animationDuration: '0.9s', animationDelay: '0.25s' }} />
            </div>
            <span className="text-sm">I'm ZAXIA, your personal concierge</span>
          </div>
        </div>
      )}

      {/* Transition Phase */}
      {phase === 'transition' && (
        <div className="flex flex-col items-center justify-center px-8 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{
              opacity: transitionTextVisible ? 1 : 0,
              y: transitionTextVisible ? 0 : 20
            }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="text-3xl md:text-4xl font-light text-white leading-relaxed max-w-4xl"
          >
            I'm here to help you discover everything around you and make your stay perfect.
          </motion.h2>
        </div>
      )}

      {/* Ambient background animation */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Gradient orbs */}
        <div className="absolute top-1/4 -left-32 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 -right-32 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>
    </div>
  );
}

export default ArrivalExperience;
